import {
  appendParams,
  convertToTimeDropDownValue,
  useFetchData,
  useParseRouteActions,
} from "../../../helpers/GlobalHelper";
import { GlobalConstants, LmsTimeDropdownOptions } from "../../constants/GlobalConstants";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import LmsTable from "../ui/LmsTable";
import LmsTabPages from "../ui/LmsTabPages";
import { guid } from "../../../Helpers";

const MainTable = () => {
  return <LmsTable 
    actionButtons={[
      "edit",
      "remove",
      "export",
      {
        text: "Update Inspection Company Project Lines",
        action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
        submitSuffix: "inspections/company-projects/nwcc-project-lines",
        method: "PUT",
        icon: GlobalConstants.Icons.CARET_UP,
        isDisabled: (checkedItems) => checkedItems?.length < 1,
      },
      {
        text: "Update And Send Inspection Company Project Lines",
        action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
        submitSuffix: "inspections/company-projects/send-nwcc-project-lines",
        method: "PUT",
        icon: GlobalConstants.Icons.CARET_UP,
        isDisabled: (checkedItems) => checkedItems?.length !== 1,
      }
    ]} 
    apiSuffix="inspections/company-projects" 
    deleteSuffix="inspections/company-projects"
    columnLabelsDictionary={{
      loanNumber: "Loan #",
    }}
  />;
};

const LmsCompanyProjectsDetailTable = ({ projectId, projectDetailId = "", suffix = "" }) => {
  const editPath = appendParams("inspections-inspection-company-projects/editdetailproject/{id}", [{ projectId }, { projectDetailId }]);
  const actionButtons = [
    { text: "Add", icon: GlobalConstants.Icons.ADD, path: `/inspections-inspection-company-projects/adddetailproject?projectId=${projectId}` },
    { text: "Edit", icon: GlobalConstants.Icons.EDIT, path: editPath, isDisabled: (checkedItems) => checkedItems?.length !== 1 },
    "remove",
    "export",
    "save",
    "cancel",
  ];

  return (
    <LmsTable
      actionButtons={actionButtons}
      onClickPath={editPath}
      apiSuffix={suffix ? suffix : `inspections/company-projects/${projectId}/details`}
      deleteSuffix="inspections/company-projects/details"
      myForm={companyProjectsDetailForm(new BCFormController())}
      saveSuffix="inspections/company-projects/details"
      columnLabelsDictionary={{
        nwccproject: "Inspection Company Project", 
        loanNumber: "Loan #"
      }}
    />
  );
};

const LmsCompanyProjectsDocumentTable = ({ projectId, documentId = "", suffix = "" }) => {
  const editPath = appendParams("inspections-inspection-company-projects/editdocumentproject/{id}", [{ projectId }, { documentId }]);
  const actionButtons = [
    { text: "Add", icon: GlobalConstants.Icons.ADD, path: `/inspections-inspection-company-projects/adddocumentproject?projectId=${projectId}` },
    { text: "Edit", icon: GlobalConstants.Icons.EDIT, path: editPath, isDisabled: (checkedItems) => checkedItems?.length !== 1 },
    "remove",
    "export"
  ];

  return (
    <LmsTable
      actionButtons={actionButtons}
      onClickPath={editPath}
      apiSuffix={suffix ? suffix : `inspections/company-projects/${projectId}/doc-logs`}
      deleteSuffix="inspections/company-project-documents"
      columnLabelsDictionary={{
        loanNumber: "Loan #"
      }}
      columnClickOptions={{
        documentLog: {
          idProp: "documentLogId",
          downloadPath: "document/download",
        },
        loanNumber: { path:`inspections-inspection-company-projects/editdocumentproject/{id}?projectId=${projectId}`, idProp: "id", },
      }}
    />
  );
};

const LmsInspectionsCompanyProjectTabPages = ({ id }) => {
  const tabPages = {
    "Detail": <LmsCompanyProjectsDetailTable projectId={id} suffix={`inspections/company-projects/${id}/details`} />,
    "Document Log": <LmsCompanyProjectsDocumentTable projectId={id} suffix={`inspections/company-projects/${id}/doc-logs`} />
  };

  return <LmsTabPages tabPages={tabPages}/>;
};

const inspectionCompanyProjectsFormFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan")]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan Property")]);
  myForm.addCenteredGroup([myForm.getInputField("Message No", { required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Project No", { required: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Submitted On")]);
  myForm.addCenteredGroup([myForm.getCalendarField("Completed On")]);
  return myForm;
};

const EditInspectionCompanyProjectsForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`inspections/company-projects/${id}`);
  const myForm = inspectionCompanyProjectsFormFields(
    new BCFormController(data, { 
      submitSuffix: "inspections/company-projects", 
      submitMethod: "PUT", 
      clearSuffixesOnSubmit: [`inspections/company-projects/${id}`],
      blankWidth: 5
    })
  );

  const formActions = ["save", "save-close", "close"];
  return (
    <>
      <LmsFormGenerator
        myForm={myForm}
        isLoading={isLoading}
        error={error}
        actions={formActions}
      />
      <LmsInspectionsCompanyProjectTabPages id={id} />
    </>
  );
};

const companyProjectsDetailForm = (myForm, projectId = "") => {
  myForm.addCenteredGroup([
    myForm.getDisplayField("Inspection Company Project", { name: "nwccProjectDisplay" }),
    myForm.getInputField("Line Number", { required: true, inlineEdit: true })
  ]);
  
  myForm.addCenteredGroup([
    myForm.getDropdownField("Loan Budget Detail", `inspections/list-loan-budget-details/${projectId}`, {
      name: "loanBudgetDetailId",
      required: true,
    }),
    myForm.getInputField("Budget Amount")
  ]);

  myForm.addCenteredGroup([
    myForm.getInputField("Percent Complete"),
    myForm.getInputField("Cumulative Percent Complete"),
  ]);

  myForm.addCenteredGroup([
    myForm.getInputField("Budget Group No"),
    myForm.getInputField("Budget Group"),
  ]);

  myForm.addCenteredGroup([myForm.getInputField("Description", { field: "textarea" })]);
  myForm.addCenteredGroup([myForm.getInputField("Comments", { name: "comment", field: "textarea" })]);
  myForm.addCenteredGroup([myForm.getInputField("Error Message", { field: "textarea" })]);
  return myForm;
};

const companyProjectsDocumentForm = (myForm) => {
  myForm.addCenteredGroup([
    myForm.getDisplayField("Inspection Company Project", { name: "nwccProjectDisplay" }),
    myForm.getDropdownField("Document Log", "document/list-document-logs", {
      name: "documentLogId",
    }),
  ]);

  myForm.addCenteredGroup([
    myForm.getCalendarField("Submitted On Date", { name: "submittedOn" }),
    myForm.getDropdownField("Submitted On Time", LmsTimeDropdownOptions, { value: convertToTimeDropDownValue(myForm?.initialValues.submittedOn) }),
  ]);

  myForm.addCenteredGroup([myForm.getInputField("Error Message", { field: "textarea" })]);
  return myForm;
};

const AddInspectionCompanyProjectsDetailForm = ({ projectId }) => {
  const [{ data: initialProjectDetail, isLoading, error }] = useFetchData(`inspections/company-projects/${projectId}/details/initial-data`);
  const myForm = new BCFormController(initialProjectDetail, { 
    submitSuffix: "inspections/company-projects/details", 
    submitMethod: "POST", 
    dataFormatterFunc: (data) => { return {...data, id: guid(), nwccprojectId: initialProjectDetail?.nwccprojectId }; },
    blankWidth: 4 
  });
  companyProjectsDetailForm(myForm, projectId);
  const formActions = ["save-new", "save-continue", "save-close", "close"];
  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    error={error}
    actions={formActions}
  />;
};

const inspectionCompanyProjectDocumentFormatter = (data, nwccprojectId = "", isAdd = false) => {
  const submitDateTime = data.submittedOn ? `${data.submittedOn?.split("T")[0]}${data.submittedOnTimeId ? data.submittedOnTimeId : "T00:00:00"}` : "";
  if (isAdd) {
    return {...data, id: guid(), nwccprojectId: nwccprojectId, submittedOn: submitDateTime };
  }
  else {
    return {...data, nwccprojectId: nwccprojectId, submittedOn: submitDateTime };
  }
};

const AddInspectionCompanyProjectsDocumentForm = ({ projectId }) => {
  const [{ data: initialProjectDocument, isLoading, error }] = useFetchData(`inspections/company-projects/${projectId}/doc-logs/initial-data`);
  const myForm = new BCFormController(initialProjectDocument, { 
    submitSuffix: "inspections/company-project-documents", 
    submitMethod: "POST", 
    dataFormatterFunc: (data) => inspectionCompanyProjectDocumentFormatter(data, initialProjectDocument?.nwccprojectId, true),
    blankWidth: 4 
  });
  companyProjectsDocumentForm(myForm);
  const formActions = ["save-new", "save-continue", "save-close", "close"];
  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    error={error}
    actions={formActions}
  />;
};

const EditInspectionCompanyProjectsDetailForm = ({ id }) => {
  const [{ data: projectDetail, isLoading, error }] = useFetchData(`inspections/company-projects/details/${id}`);
  const myForm = new BCFormController(projectDetail, { 
    submitSuffix: "inspections/company-projects/details",
    submitMethod: "PUT", 
    dataFormatterFunc: (data) => { return {...data, nwccprojectId: projectDetail?.nwccprojectId }; },
    clearSuffixesOnSubmit: [`inspections/company-projects/details/${id}`],
    deleteSuffix: "inspections/company-projects/details",
    blankWidth: 4 
  });
  if (projectDetail) { companyProjectsDetailForm(myForm, projectDetail?.nwccprojectId); }
  const formActions = ["save", "save-close", "delete", "close"];
  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    error={error}
    actions={formActions}
  />;
};

const EditInspectionCompanyProjectsDocumentForm = ({ id }) => {
  const [{ data, isLoading, error }] = useFetchData(`inspections/company-projects/doc-logs/${id}`);
  const myForm = new BCFormController(data, { 
    submitSuffix: "inspections/company-project-documents",
    submitMethod: "PUT", 
    dataFormatterFunc: (data) => inspectionCompanyProjectDocumentFormatter(data, id),
    deleteSuffix: "inspections/company-project-documents",
    clearSuffixesOnSubmit: [`inspections/company-projects/doc-logs/${id}`],
    blankWidth: 4 
  });
  companyProjectsDocumentForm(myForm);
  const formActions = ["save", "save-close", "delete", "close"];
  return <LmsFormGenerator
    myForm={myForm}
    isLoading={isLoading}
    error={error}
    actions={formActions}
  />;
};

const LmsInspectionsInspectionCompanyProjects = ({ params = {}, history }) => {
  const allowedSubpages = ["adddetailproject", "editdetailproject", "adddocumentproject", "editdocumentproject"];
  const { isMainPage, isEdit, isActionAllowed, id, isSubpageAllowed, actionLower, query } = useParseRouteActions(params, ["edit"], allowedSubpages);
  const projectId = query.get("projectId") || "";

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <MainTable />}
      {isEdit && <EditInspectionCompanyProjectsForm id={id} />} 
      {isSubpageAllowed && (
        <>
          {actionLower === "adddetailproject" && (
            <AddInspectionCompanyProjectsDetailForm projectId={projectId} />
          )}
          {actionLower === "editdetailproject" && (
            <EditInspectionCompanyProjectsDetailForm id={id} />
          )}
          {actionLower === "adddocumentproject" && (
            <AddInspectionCompanyProjectsDocumentForm projectId={projectId} />
          )}
          {actionLower === "editdocumentproject" && (
            <EditInspectionCompanyProjectsDocumentForm id={id} />
          )}
        </>
      )}
    </>
  );
};

export default LmsInspectionsInspectionCompanyProjects;
